import { Box, Paper, Typography, useTheme } from '@mui/material';
import { formatMetricValue } from '../../utils/formatMetricValue';
import TileMetaActions from '../TileMetaActions';
import { cardShellSx, getMetricDisplayName, resolveDetailLevel } from './rendererTypes';
import type { MetricRendererProps } from './rendererTypes';
import { tileMetaTitlePad } from '../../utils/tileMeta';

export default function StatWithDescriptionRenderer({ config, data }: MetricRendererProps) {
  const theme = useTheme();
  const displayValue = formatMetricValue(data.value, data.format ?? config.format);
  const title = getMetricDisplayName(data, config);

  return (
    <Paper elevation={0} sx={cardShellSx(theme.palette.divider, theme.palette.background.paper)}>
      <TileMetaActions query={data.meta?.query} registry={data.meta?.registry} />
      {title ? (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            lineHeight: 1.4,
            mb: 1,
            pr: tileMetaTitlePad(data.meta),
          }}
        >
          {title}
        </Typography>
      ) : null}
      <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.1, mb: 1 }}>
        {displayValue}
      </Typography>
      <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.5 }}>
        {data.description}
      </Typography>
    </Paper>
  );
}

export function StatWithBreakdownRenderer({ config, data, presentation }: MetricRendererProps) {
  const theme = useTheme();
  const displayValue = formatMetricValue(data.value, data.format ?? config.format);
  const title = getMetricDisplayName(data, config);
  const showSupplementalDetails = resolveDetailLevel(presentation) === 'full';
  const showBreakdown = showSupplementalDetails && Boolean(data.breakdown?.length);

  return (
    <Paper elevation={0} sx={cardShellSx(theme.palette.divider, theme.palette.background.paper)}>
      <TileMetaActions query={data.meta?.query} registry={data.meta?.registry} />
      {title ? (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            fontWeight: 600,
            lineHeight: 1.4,
            mb: 1,
            pr: tileMetaTitlePad(data.meta),
          }}
        >
          {title}
        </Typography>
      ) : null}
      <Typography variant="h3" sx={{ fontWeight: 700, color: 'primary.main', lineHeight: 1.1, mb: 1 }}>
        {displayValue}
      </Typography>
      <Typography
        variant="body2"
        sx={{ color: 'text.secondary', lineHeight: 1.5, mb: showBreakdown ? 2 : 0 }}
      >
        {data.description}
      </Typography>
      {showBreakdown && data.breakdown ? (
        <Box sx={{ mt: 'auto' }}>
          {data.breakdown.map(row => (
            <Box
              key={row.label}
              sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}
            >
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {row.label}
              </Typography>
              <Typography variant="caption" sx={{ fontWeight: 600 }}>
                {row.pct != null ? `${row.pct}%` : row.value}
              </Typography>
            </Box>
          ))}
        </Box>
      ) : null}
    </Paper>
  );
}
